<template>
  <!--
    批量绑定候选弹框。
    HTML 中的 $t('message.xxx') 是 vue-i18n 多语言取值：message 是文案对象，xxx 是文案 key。
    例如：$t('message.search') 取“搜索”，$t('message.reset') 取“重置”，切换语言后会自动取日文文案。
  -->
  <!-- $t('message.batchBindOrderPlan')：弹框标题“批量绑定下单方案”。 -->
  <el-dialog :title="$t('message.batchBindOrderPlan')" :visible.sync="visible" width="1300px" append-to-body
    :close-on-click-modal="false">
    <div class="candidate-search">
      <!-- $t('message.productManagementNumber')：管理番号输入框提示和表头文案。 -->
      <el-input v-model.trim="filters.client_sku_code" :placeholder="$t('message.productManagementNumber')"
        clearable @keyup.enter.native="search" />
      <!-- $t('message.orderPlanTagNo')：方案标签号输入框提示。 -->
      <el-input v-model.trim="filters.label_no" :placeholder="$t('message.orderPlanTagNo')" clearable
        @keyup.enter.native="search" />
      <!-- $t('message.orderPlanRemark')：方案业务备注输入框提示。 -->
      <el-input v-model.trim="filters.y_remark" :placeholder="$t('message.orderPlanRemark')" clearable
        @keyup.enter.native="search" />
      <!-- $t('message.search')：搜索按钮文字。 -->
      <el-button type="primary" :loading="loading" @click="search">{{ $t('message.search') }}</el-button>
      <!-- $t('message.reset')：重置筛选条件按钮文字。 -->
      <el-button @click="reset">{{ $t('message.reset') }}</el-button>
    </div>

    <!-- 每条下单方案对应表格一行，方案名称和该方案的 materials 在同一行展示。 -->
    <el-table v-loading="loading" :data="planRows" :span-method="spanPlanRows" border height="500"
      :header-cell-style="{ background: '#EDEFF4' }">
      <!-- $t('message.productManagementNumber')：商品管理番号表头。 -->
      <el-table-column :label="$t('message.productManagementNumber')" align="center" width="200">
        <template slot-scope="scope">
          <a href="#" class="a-link" @click.prevent="openProductManagement(scope.row.management)">
            {{ scope.row.management.client_sku_code }}
          </a>
        </template>
      </el-table-column>
      <el-table-column width="76" align="center">
        <template slot="header">
          <!-- $t('message.selectAll')：全选复选框文字。 -->
          <el-checkbox :value="isAllSelected" :indeterminate="isSomeSelected" @change="toggleAll">
            {{ $t('message.selectAll') }}
          </el-checkbox>
        </template>
        <template slot-scope="scope">
          <div class="plan-select-row">
            <el-checkbox :value="isPlanSelected(scope.row.plan)"
              @change="togglePlan(scope.row.management, scope.row.plan, $event)" />
          </div>
        </template>
      </el-table-column>
      <!-- $t('message.orderPlan')：下单方案表头。 -->
      <el-table-column :label="$t('message.orderPlan')" min-width="230">
        <template slot-scope="scope">
          <div class="plan-name-cell">{{ scope.row.plan.name }}</div>
        </template>
      </el-table-column>
      <!-- $t('message.boundMaterialList')：已绑定辅料表头。 -->
      <el-table-column :label="$t('message.boundMaterialList')" min-width="580">
        <template slot-scope="scope">
          <div class="material-cards">
            <div v-for="material in scope.row.plan.materials" :key="material.material_id" class="material-card"
              @click="openMaterialDetail(material)">
              <el-image :src="material.main_image" fit="cover" class="material-image" />
              <div class="material-card-info">
                <span class="material-name">{{ material.name }}</span>
                <!-- $t('message.materialConsumption')：消耗标签；$t('message.pointsPerSet')：点/套单位。 -->
                <span>{{ $t('message.materialConsumption') }}：{{ material.consume }} {{ $t('message.pointsPerSet') }}</span>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="candidate-pagination">
      <el-pagination @current-change="handleCurrentChange" :current-page="page" :page-size="pageSize"
        layout="total, prev, pager, next, jumper" :total="total" />
    </div>

    <span slot="footer">
      <!-- $t('message.cancel')：取消按钮文字。 -->
      <el-button @click="visible = false">{{ $t('message.cancel') }}</el-button>
      <!-- $t('message.batchBind')：批量绑定按钮文字。 -->
      <el-button type="primary" :loading="saving" @click="confirm">{{ $t('message.batchBind') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'BatchBindOrderPlanDialog',
  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      materialId: '',
      filters: {
        client_sku_code: '',
        label_no: '',
        y_remark: ''
      },
      candidateList: [],
      selectedPlans: [],
      page: 1,
      pageSize: 50,
      total: 0
    }
  },
  computed: {
    // 将每个管理番号下的 plans 展开成表格行，让方案和 materials 一行一一对应。
    planRows() {
      return this.candidateList.reduce((rows, management) => {
        management.plans.forEach((plan, index) => {
          rows.push({
            management,
            plan,
            rowSpan: index === 0 ? management.plans.length : 0
          })
        })
        return rows
      }, [])
    },
    // 当前页所有方案的 ID，用于计算全选和半选状态。
    currentPlanKeys() {
      return this.planRows.map(row => row.plan.plan_id)
    },
    // 当前页的所有下单方案是否全部勾选。
    isAllSelected() {
      return this.currentPlanKeys.length > 0 && this.currentPlanKeys.every(key => this.isSelectedKey(key))
    },
    // 当前页存在部分方案被勾选时显示半选状态。
    isSomeSelected() {
      return this.currentPlanKeys.some(key => this.isSelectedKey(key)) && !this.isAllSelected
    }
  },
  methods: {
    // 打开弹框时传入当前辅料主档 ID，候选接口用它定位归属客户。
    open(materialId) {
      this.materialId = materialId
      this.visible = true
      this.selectedPlans = []
      this.reset()
    },
    // 候选接口筛选参数与后端字段保持一致：client_sku_code、label_no、y_remark。
    search() {
      this.page = 1
      this.loadCandidates()
    },
    // 清空管理番号、标签号和业务备注筛选，并重新加载第一页。
    reset() {
      this.filters = {
        client_sku_code: '',
        label_no: '',
        y_remark: ''
      }
      this.page = 1
      this.loadCandidates()
    },
    // 调用候选列表接口，直接使用接口返回的 data 和 total 字段渲染表格。
    loadCandidates() {
      const params = {
        id: this.materialId,
        client_sku_code: this.filters.client_sku_code,
        label_no: this.filters.label_no,
        y_remark: this.filters.y_remark,
        page: this.page,
        pageSize: this.pageSize
      }
      this.loading = true
      this.$api.warehouseApi.orderPlanBindCandidateList(params).then(res => {
        const data = res.data
        this.candidateList = data.data
        this.total = data.total
      }).finally(() => {
        this.loading = false
      })
    },
    // 判断指定下单方案是否已被选中。
    isSelectedKey(key) {
      return this.selectedPlans.some(item => item.key === key)
    },
    // 根据下单方案 ID返回勾选状态。
    isPlanSelected(plan) {
      return this.isSelectedKey(plan.plan_id)
    },
    // 添加或移除单个下单方案的选择状态。
    togglePlan(row, plan, checked) {
      const key = plan.plan_id
      const selectedIndex = this.selectedPlans.findIndex(item => item.key === key)
      if (checked && selectedIndex === -1) {
        this.selectedPlans.push({ key, row, plan })
      } else if (!checked && selectedIndex !== -1) {
        this.selectedPlans.splice(selectedIndex, 1)
      }
    },
    // 切换当前页所有下单方案的勾选状态。
    toggleAll(checked) {
      this.planRows.forEach(row => this.togglePlan(row.management, row.plan, checked))
    },
    // 调用批量绑定接口，提交当前辅料 ID 和勾选的主下单方案 ID。
    confirm() {
      this.saving = true
      this.$api.warehouseApi.batchBindOrderPlan({
        id: this.materialId,
        plan_ids: this.selectedPlans.map(item => item.plan.plan_id)
      }).then(res => {
        this.$message.success(res.msg)
        this.$emit('saved')
        this.visible = false
      }).finally(() => {
        this.saving = false
      })
    },
    // 在新标签页打开商品管理，并使用当前管理番号作为查询条件。
    openProductManagement(row) {
      const route = this.$router.resolve({
        name: 'productManagementV2',
        query: {
          client_sku_code: row.client_sku_code
        }
      })
      window.open(route.href, '_blank')
    },
    // 点击已绑定辅料卡片，新开辅料详情页
    openMaterialDetail(material) {
      const id = material.material_id || material.id
      if (!id) return
      const route = this.$router.resolve({
        name: 'materialDetail',
        query: {
          id,
          user_id: material.user_id || this.$route.query.user_id || ''
        }
      })
      window.open(route.href, '_blank')
    },
    // 合并同一个管理番号的多行，管理番号只在第一行显示。
    spanPlanRows({ row, columnIndex }) {
      if (columnIndex === 0) return [row.rowSpan, 1]
      return [1, 1]
    },
    // 切换候选管理番号分页。
    handleCurrentChange(page) {
      this.page = page
      this.loadCandidates()
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-search {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;

  .el-input {
    width: 220px;
  }
}

/deep/ .el-table__body td {
  vertical-align: top;
}

// 放大方案选择和表头全选复选框，方便点击。
/deep/ .el-checkbox__inner {
  width: 18px;
  height: 18px;
}

/deep/ .el-checkbox__inner::after {
  width: 5px;
  height: 10px;
  left: 5px;
  top: 2px;
}

.plan-select-row,
.plan-name-cell {
  min-height: 56px;
  padding: 8px 0;
  box-sizing: border-box;
}

.plan-select-row {
  display: flex;
  align-items: center;
  justify-content: center;
}

.plan-name-cell {
  color: #303133;
  line-height: 20px;
}

.material-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.material-card {
  display: flex;
  align-items: center;
  width: 220px;
  min-height: 48px;
  padding: 5px 8px;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  box-sizing: border-box;
  cursor: pointer;
}

.material-image {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  margin-right: 8px;
  background: #F5F7FA;
}

.material-card-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: #606266;
  font-size: 12px;
  line-height: 18px;
}

.material-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unbound-text {
  color: #B4272D;
}

.candidate-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
